<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">

    <div class="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ form.id ? 'Editar Categoría' : 'Nueva Categoría' }}
          </h2>
          <p class="text-xs text-slate-500">Clasificación de productos.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="p-6 space-y-5">

        <div>
          <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Nombre Categoría</label>
          <div class="relative">
            <Tag class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              v-model="form.name"
              type="text"
              required
              class="form-input-premium pl-10"
              placeholder="Ej: Antibióticos, Bebidas..."
              autofocus
            />
          </div>
        </div>

        <div>
          <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Descripción (Opcional)</label>
          <textarea
            v-model="form.description"
            rows="3"
            class="form-input-premium resize-none"
            placeholder="Detalles adicionales para reportes..."
          ></textarea>
        </div>

        <div class="flex justify-end pt-2">
          <label class="flex items-center gap-2 cursor-pointer select-none">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ form.is_active ? 'Categoría Activa' : 'Archivada' }}
            </span>
            <div class="relative">
              <input type="checkbox" v-model="form.is_active" class="sr-only peer">
              <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </div>
          </label>
        </div>

        <div class="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="isSaving"
            class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center gap-2 disabled:opacity-70"
          >
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch } from 'vue';
import { X, Save, Loader2, Tag } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  show: Boolean,
  categoryToEdit: Object
});

const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError, showWarning } = useToast();

const isSaving = ref(false);
const currentUser = SecureStorage.getItem('llevo_user');
const currentCompanyId = currentUser?.company;

const form = reactive({
  id: null,
  company: currentCompanyId,
  name: '',
  description: '',
  is_active: true
});

watch(() => props.categoryToEdit, (newVal) => {
  if (newVal) {
    Object.assign(form, {
      id: newVal.id,
      company: newVal.company || currentCompanyId,
      name: newVal.name,
      description: newVal.description,
      is_active: Boolean(newVal.is_active)
    });
  } else {
    Object.assign(form, {
      id: null,
      company: currentCompanyId,
      name: '',
      description: '',
      is_active: true
    });
  }
});

const handleSubmit = async () => {
  if (!form.name.trim()) {
    showWarning('El nombre es obligatorio.');
    return;
  }

  isSaving.value = true;
  try {
    const payload = { ...form };

    if (form.id) {
      await apiClient.put(`/inventory_categories/${form.id}`, payload);
      showSuccess('Categoría actualizada.');
    } else {
      await apiClient.post('/inventory_categories', payload);
      showSuccess('Categoría creada.');
    }
    emit('saved');
    emit('close');
  } catch (error) {
    console.error(error);
    showError('Error al guardar la categoría.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}
</style>